<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from '@vue/composition-api';

import { useCurrentUserModule } from '@/store/global/currentUser';
import { queryExecutor } from '@/_core/cqrs';
import { AccountQuery } from '@/api/account/queries';
import { useAccountStore, InitAccount } from '@/store/global/account';

import AccountForm from './AccountForm.vue';
import ChangePasswordForm from './ChangePasswordForm.vue';
import DeleteAccount from './DeleteAccount.vue';
import McpConsents from './McpConsents.vue';
import EditorPreferences from './EditorPreferences.vue';

interface IProps {
    isOpen: boolean;
}

const initStore = async () => {
    //TODO: refactor AccountQuery !!!
    const res = await queryExecutor.executeQuery(new AccountQuery());

    const store = useAccountStore();
    await store.dispatch(new InitAccount(res));

    return;
};

const UserProfile = defineComponent({
    name: 'UserProfile',
    components: { AccountForm, ChangePasswordForm, DeleteAccount, McpConsents, EditorPreferences },
    props: { isOpen: Boolean },

    setup(props, ctx) {
        const store = useCurrentUserModule();

        const state = reactive({
            loading: true,
            isShowChangePassword: computed(() => !store.state.ssoEnabledForUser),
            isShowDeleteAccount: computed(() => !store.state.ssoEnabledForUser && !store.state.userIsDomainControlled),
        });

        const tab = ref<number | null>(null);

        const tabOrder = computed(() => {
            const order: string[] = ['account'];
            if (state.isShowChangePassword) {
                order.push('password');
            }
            order.push('mcp');
            order.push('preferences');
            if (state.isShowDeleteAccount) {
                order.push('delete');
            }

            return order;
        });

        initStore().then(() => (state.loading = false));

        watch(
            () => props.isOpen,
            (currentValue) => {
                if (currentValue) {
                    initStore();
                }
            },
            { immediate: true }
        );

        const close = () => {
            ctx.emit('close');
        };

        const tabIndexOf = (key: string) => {
            return tabOrder.value.indexOf(key);
        };

        return {
            close,
            tab,
            state,
            tabIndexOf,
        };
    },
});

export default UserProfile;
</script>

<template>
  <AppDialog
    title="Profile"
    width="1084"
    :isOpen="isOpen"
    @close="close"
  >
    <v-progress-linear
      :active="state.loading"
      indeterminate        
      color="primary lighten-2"
      class="mt-1"
    />

    <v-card-text style="min-height: 250px">
      <v-fade-transition mode="out-in">
        <v-skeleton-loader
          v-if="state.loading"
          boilerplate
          class="ma-2"
          height="350"
          type="list-item-avatar-three-line, card"
        />

        <v-tabs v-if="!state.loading" v-model="tab" show-arrows>
          <v-tab>
            <v-icon left>
              $team-light
            </v-icon>Account
          </v-tab>

          <v-tab v-if="state.isShowChangePassword">
            <v-icon left>
              $fasUnlockAlt
            </v-icon>Change Password
          </v-tab>

          <v-tab>
            <v-icon left>
              mdi-shield-link-variant-outline
            </v-icon>Connected Apps
          </v-tab>

          <v-tab>
            <v-icon left>
              mdi-tune
            </v-icon>Preferences
          </v-tab>

          <v-tab v-if="state.isShowDeleteAccount">
            <v-icon left>
              $fasUserTimes
            </v-icon>Delete your account
          </v-tab>

          <v-tab-item>
            <AccountForm @close="close" />
          </v-tab-item>

          <v-tab-item v-if="state.isShowChangePassword">
            <ChangePasswordForm @close="close" />
          </v-tab-item>

          <v-tab-item>
            <McpConsents />
          </v-tab-item>

          <v-tab-item>
            <EditorPreferences :active="tab === tabIndexOf('preferences')" />
          </v-tab-item>

          <v-tab-item v-if="state.isShowDeleteAccount">
            <DeleteAccount />
          </v-tab-item>
        </v-tabs>
      </v-fade-transition>
    </v-card-text>
  </AppDialog>   
</template>
